import { useAuth } from "@/contexts/AuthContext";
import { canView, isGlobalSuperAdmin } from "@/lib/permissions";

/**
 * Unified access control hook that works for both regular users and staff.
 *
 * The core problem this solves: regular users have `memberships` with role info,
 * but staff users have `isStaff=true` with permissions directly in their JWT
 * and an empty `memberships` array. Many pages checked access via
 * `user.memberships.some(m => m.roleName === "Tenant Admin")` which always
 * returns false for staff.
 *
 * This hook provides a consistent API:
 * - `hasPermission(perm)` - checks user has a specific permission (works for both)
 * - `hasAnyPermission(perms)` - checks user has at least one of the permissions
 * - `isTenantAdmin` - true if user is a tenant admin (via membership OR staff role)
 * - `isAdminOrHasPermission(perm)` - shorthand: admin access OR specific permission
 */
export function useAccessControl() {
  const { user } = useAuth();

  const permissions = user?.permissions || [];
  const isSuperAdminUser = isGlobalSuperAdmin(user);

  const hasPermission = (perm: string): boolean => {
    if (!user) return false;
    if (isSuperAdminUser) return true;
    return canView(perm, permissions);
  };

  const hasAnyPermission = (perms: string[]): boolean => {
    if (!user) return false;
    if (isSuperAdminUser) return true;
    return perms.some((p) => canView(p, permissions));
  };

  const isTenantAdmin: boolean = (() => {
    if (!user) return false;
    if (isSuperAdminUser) return true;

    // Staff authenticated via staff login
    if (user.isStaff) {
      const staffRole = user.roleName?.toUpperCase()?.replace(/\s+/g, "_") || "";
      return staffRole.includes("TENANT") && staffRole.includes("ADMIN");
    }

    // Regular user via membership
    return (
      user.memberships?.some(
        (m) =>
          m.roleName === "Tenant Admin" ||
          m.role === "TENANT_ADMIN" ||
          m.role?.toUpperCase() === "TENANT_ADMIN"
      ) || false
    );
  })();

  /**
   * Check if user is tenant admin OR has a specific permission.
   * Use this for pages that admins always see but staff need a permission for.
   */
  const isAdminOrHasPermission = (perm: string): boolean => {
    return isTenantAdmin || hasPermission(perm);
  };

  return {
    user,
    permissions,
    hasPermission,
    hasAnyPermission,
    isTenantAdmin,
    isAdminOrHasPermission,
  };
}
